import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { format, parseISO } from "date-fns";
import { weekStartOf } from "@alm/domain";
import { PageHeader } from "@/components/app/shell/page-header";
import { PrintedDocument } from "@/components/app/documents/printed-document";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useWeekRota } from "@/hooks/use-week-rota";
import { useFacility } from "@/lib/facility/facility";
import { Printer } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { buildBoard, weekOf, WHOLE_BUILDING } from "./-components/board-model";
import { CONTACT_FIELDS, type ContactField, type PrintLayout } from "./-components/print-dialog";
import { PrintedWeek } from "./-components/printed-week";

const DAY = /^\d{4}-\d{2}-\d{2}$/;

export const Route = createFileRoute("/_authenticated/_workforce/schedule/print")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { week?: string; department?: string; list?: string; layout?: PrintLayout } => ({
    week: typeof search.week === "string" && DAY.test(search.week) ? search.week : undefined,
    department:
      typeof search.department === "string" && search.department ? search.department : undefined,
    list: typeof search.list === "string" && search.list ? search.list : undefined,
    layout: search.layout === "each" ? "each" : undefined,
  }),
  component: PrintSchedule,
});

/** The week on paper, landscape: the whole building on one sheet or a sheet per department, and the
 * people on it under it with whichever details were asked for. The print dialog opens on its own
 * once everything has loaded; the button is for a second copy. */
function PrintSchedule() {
  const { facilityId } = useFacility();
  const search = Route.useSearch();
  const weekStart = weekStartOf(search.week ?? format(new Date(), "yyyy-MM-dd"));
  const days = useMemo(() => weekOf(weekStart), [weekStart]);
  const fields = (search.list ?? "")
    .split(",")
    .filter((entry): entry is ContactField =>
      (CONTACT_FIELDS as readonly string[]).includes(entry),
    );
  const withList = search.list !== undefined;

  const { patterns, rota, staff } = useWeekRota(facilityId, weekStart);
  const buildings = useQuery(
    orpc.administration.facilities.list.queryOptions({ input: { includeInactive: false } }),
  );
  const ready =
    patterns.data !== undefined &&
    rota.data !== undefined &&
    (!withList || staff.data !== undefined);

  const sections = useMemo(() => {
    const all = buildBoard(patterns.data ?? [], rota.data ?? []);
    return search.department
      ? all.filter((section) => (section.department ?? WHOLE_BUILDING) === search.department)
      : all;
  }, [patterns.data, rota.data, search.department]);
  const facilityName = buildings.data?.find((entry) => entry.id === facilityId)?.name ?? "Schedule";
  /* The list under the week is the people on it, not the whole roster: a
     sheet on the wall answers "who do I call about tonight", and a leaver
     or somebody off this week is noise there. */
  const onTheWeek = useMemo(() => {
    const ids = new Set<string>();
    for (const section of sections) {
      for (const row of section.rows) {
        for (const list of row.cells.values()) {
          for (const shift of list) if (shift.status !== "cancelled") ids.add(shift.userId);
        }
      }
    }
    return ids;
  }, [sections]);
  const listed = withList ? (staff.data ?? []).filter((person) => onTheWeek.has(person.id)) : null;

  /* One print per load, once the sheet is complete; a second copy is the button. */
  const printed = useRef(false);
  useEffect(() => {
    if (!ready || printed.current) return;
    printed.current = true;
    const timer = window.setTimeout(() => window.print(), 300);
    return () => window.clearTimeout(timer);
  }, [ready]);

  return (
    <>
      <div className="mb-4 print:hidden">
        <PageHeader
          title="Print the schedule"
          meta={`${format(parseISO(weekStart), "d MMM")} to ${format(parseISO(days[6]!), "d MMM yyyy")}${search.department ? `, ${search.department}` : ", everyone"}`}
          actions={
            <Button disabled={!ready} onClick={() => window.print()}>
              <Printer className="size-4" />
              Print
            </Button>
          }
        />
      </div>
      {ready ? (
        <PrintedDocument orientation="landscape">
          <PrintedWeek
            days={days}
            facilityName={facilityName}
            fields={fields}
            layout={search.layout ?? "one"}
            sections={sections}
            staff={listed}
            weekStart={weekStart}
          />
        </PrintedDocument>
      ) : (
        <Skeleton className="h-96 w-full" />
      )}
    </>
  );
}
